import React, { useEffect, useState } from 'react';
import { http_get, http_post, http_put } from '../../../utils/api_client';
import { API_ENDPOINTS } from '../../../../pages/utils/endpoints';

// BL-068 + BL-069 — the custom-domain add wizard, matching the redesign mockup.
// Unifies add / edit / fix:
//   add  → create the domain, then poll verification
//   edit → update the name, then poll verification
//   fix  → re-run verification for an existing (errored) domain
// Steps: 1 Domain · 2 DNS · 3 Verify · 4 Done.

const PROVIDERS = ['Cloudflare', 'GoDaddy', 'Namecheap', 'Google Domains', 'Other'];
const POLL_MS = 6000;

const providerInstructions = (provider, r) => {
  switch (provider) {
    case 'Cloudflare':
      return (<><b>Cloudflare:</b> DNS → Records → Add record → Type <b>{r.type}</b>, Name <b>{r.name}</b>, Target <b>{r.value}</b>. Set Proxy status to <b>DNS only</b> (grey cloud), then Save.</>);
    case 'GoDaddy':
      return (<><b>GoDaddy:</b> Domain portfolio → <b>{r.apex}</b> → DNS → Add → Type <b>{r.type}</b>, Name <b>{r.name}</b>, Value <b>{r.value}</b> → Save.</>);
    case 'Namecheap':
      return (<><b>Namecheap:</b> Domain List → Manage → Advanced DNS → Add New Record → Type <b>{r.type}</b>, Host <b>{r.name}</b>, Value <b>{r.value}</b> → Save.</>);
    case 'Google Domains':
      return (<><b>Google / Squarespace Domains:</b> DNS → Resource records → Add → Type <b>{r.type}</b>, Host <b>{r.name}</b>, Data <b>{r.value}</b> → Save.</>);
    default:
      return (<>Add a <b>{r.type}</b> record with Name <b>{r.name}</b> pointing to <b>{r.value}</b> at your DNS provider.</>);
  }
};

const AddDomainWizard = ({ mode = 'add', existingDomain = null, targets = {}, onClose, onComplete }) => {
  const [step, setStep] = useState(mode === 'fix' ? 2 : 1);
  const [domainName, setDomainName] = useState(existingDomain?.name || '');
  const [domainId, setDomainId] = useState(existingDomain?.id || null);
  const [provider, setProvider] = useState('Cloudflare');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [makePrimary, setMakePrimary] = useState(true);
  const [copied, setCopied] = useState('');

  const clean = domainName.trim().toLowerCase();
  const labels = clean.split('.').filter(Boolean);
  const apex = labels.length <= 2;
  const record = {
    type: apex ? 'A' : 'CNAME',
    name: apex ? '@' : labels.slice(0, -2).join('.'),
    value: apex ? (targets.a_target || '') : (targets.cname_target || ''),
    apex: labels.slice(-2).join('.')
  };
  const domainValid = labels.length >= 2 && /^[a-z0-9.-]+$/.test(clean);

  // Step 3 — poll live verification status until verified.
  useEffect(() => {
    if (step !== 3 || !domainId) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const res = await http_get(API_ENDPOINTS.DOMAIN_STATUS(domainId));
        if (!active) return;
        const data = res.data || {};
        setStatusData(data);
        if (data.verification_status === 'verified') setStep(4);
      } catch (e) { /* keep polling */ }
    };
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => { active = false; clearInterval(timer); };
  }, [step, domainId]);

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(''), 1200);
  };

  const goVerify = async () => {
    setError('');
    setSubmitting(true);
    try {
      let id = domainId;
      if (mode === 'add') {
        const res = await http_post(API_ENDPOINTS.POST_DOMAIN, { domain_name: clean });
        const data = res.data || {};
        if (data.error || res.status >= 300) { setError(data.error || 'Could not add that domain.'); return; }
        const list = await http_get(API_ENDPOINTS.GET_DOMAINS);
        id = (list.data || []).find((d) => (d.name || '').toLowerCase() === clean)?.id;
        if (!id) { setError('Domain created, but we could not load it — check the list.'); return; }
      } else if (mode === 'edit') {
        const res = await http_put(API_ENDPOINTS.UPDATE_DOMAIN(id), { domain_name: clean });
        if (res.data?.error) { setError(res.data.error); return; }
      } else {
        await http_post(API_ENDPOINTS.RECONNECT_DOMAIN, { domain_id: id });
      }
      setDomainId(id);
      setStatusData(null);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const recheck = async () => {
    if (!domainId) return;
    try { await http_post(API_ENDPOINTS.RECONNECT_DOMAIN, { domain_id: domainId }); } catch (e) { /* noop */ }
  };

  const finish = async () => {
    if (makePrimary && domainId) {
      try { await http_put(API_ENDPOINTS.SET_PRIMARY_DOMAIN(domainId), {}); } catch (e) { /* noop */ }
    }
    onComplete();
  };

  const stepState = (n) => (step > n ? 'done' : step === n ? 'on' : '');
  const num = (n) => (step > n ? '✓' : n);

  const Stepper = () => (
    <div className='stepper'>
      <div className={`s ${stepState(1)}`}><span className='n'>{num(1)}</span><span className='lb'>Domain</span></div>
      <span className={`line ${step > 1 ? 'done' : ''}`} />
      <div className={`s ${stepState(2)}`}><span className='n'>{num(2)}</span><span className='lb'>DNS</span></div>
      <span className={`line ${step > 2 ? 'done' : ''}`} />
      <div className={`s ${stepState(3)}`}><span className='n'>{num(3)}</span><span className='lb'>Verify</span></div>
      <span className={`line ${step > 3 ? 'done' : ''}`} />
      <div className={`s ${stepState(4)}`}><span className='n'>{num(4)}</span><span className='lb'>Done</span></div>
    </div>
  );

  const CopyRow = ({ k, v }) => (
    <div className='rr'>
      <span className='k'>{k}</span>
      <span className='copy' onClick={() => copy(v)}>{v}<i className='c'>{copied === v ? '✓' : '⧉'}</i></span>
    </div>
  );

  const isError = statusData?.verification_status === 'error';

  return (
    <div className='un-domains__overlay' onClick={onClose}>
      <div className='un-domains__wizard' onClick={(e) => e.stopPropagation()}>
        <div className='mh'>
          <span className='ttl'>
            {step === 1 && 'Add a custom domain'}
            {step === 2 && 'Add your DNS record'}
            {step === 3 && (isError ? 'Almost there' : 'Verifying…')}
            {step === 4 && 'Domain connected'}
          </span>
          <span className='x' onClick={onClose}>✕</span>
        </div>

        <Stepper />

        {step === 1 && (
          <>
            <div className='mbody'>
              <label className='lab'>Domain or subdomain</label>
              <input
                className='input'
                autoFocus
                placeholder='e.g. onboarding.acme.com'
                value={domainName}
                onChange={(e) => setDomainName(e.target.value)}
              />
              {domainValid && (
                <div className='hint good'>✓ Looks good — this is {apex ? 'a root domain, so we’ll set up an A record' : 'a subdomain, so we’ll set up a CNAME record'} next.</div>
              )}
              {error && <div className='formerr'>{error}</div>}
            </div>
            <div className='mfoot'>
              <button className='btn btn-secondary btn-sm' onClick={onClose} type='button'>Cancel</button>
              <button className='btn btn-primary btn-sm' disabled={!domainValid} onClick={() => { setError(''); setStep(2); }} type='button'>Continue</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className='mbody'>
              <p className='sub'>Add this single record at your DNS provider for <b>{clean}</b>:</p>
              <div className='rec'>
                <CopyRow k='Type' v={record.type} />
                <CopyRow k='Name' v={record.name} />
                <CopyRow k='Value' v={record.value} />
              </div>
              <div className='prov'>
                {PROVIDERS.map((p) => (
                  <span key={p} className={`p ${provider === p ? 'on' : ''}`} onClick={() => setProvider(p)}>{p}</span>
                ))}
              </div>
              <div className='provbox'>{providerInstructions(provider, record)}</div>
              <div className='callout'>Keep this open — pop over to your DNS provider in another tab and come back. We’ll auto-detect the record.</div>
              {error && <div className='formerr'>{error}</div>}
            </div>
            <div className='mfoot'>
              {mode !== 'fix' && <button className='btn btn-secondary btn-sm' onClick={() => setStep(1)} type='button'>Back</button>}
              <button className='btn btn-primary btn-sm' disabled={submitting} onClick={goVerify} type='button'>
                {submitting ? 'Working…' : 'I’ve added it — verify'}
              </button>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div className='mbody'>
              {isError ? (
                <div className='verify' style={{ paddingTop: 6 }}>
                  <h5 className='danger'>Record not matching yet</h5>
                  <div className='verr'><span className='vi'>!</span><div>{statusData?.last_error || 'The record doesn’t match yet. Update it at your provider; we’ll keep checking.'}</div></div>
                </div>
              ) : (
                <div className='verify'>
                  <div className='spin' />
                  <h5>Checking DNS for {clean}</h5>
                  <p>This can take a few minutes to a few hours. We check every 60 seconds — you can close this and we’ll keep verifying.</p>
                </div>
              )}
            </div>
            <div className='mfoot'>
              {isError
                ? <button className='btn btn-secondary btn-sm' onClick={() => setStep(2)} type='button'>Back to instructions</button>
                : <button className='btn btn-secondary btn-sm' onClick={onClose} type='button'>Close</button>}
              <button className='btn btn-primary btn-sm' onClick={recheck} type='button'>Recheck</button>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div className='mbody'>
              <div className='verify'>
                <div className='okmark'>✓</div>
                <h5>{clean} is live</h5>
                <p>Your content now serves from your own domain.</p>
              </div>
              <div className='toggle' onClick={() => setMakePrimary((v) => !v)}>
                <div>
                  <div className='t1'>Set as primary domain</div>
                  <div className='t2'>Learners are sent here by default.</div>
                </div>
                <span className={`sw ${makePrimary ? 'on' : ''}`} />
              </div>
            </div>
            <div className='mfoot'>
              <button className='btn btn-secondary btn-sm' onClick={() => window.open(`https://${clean}`, '_blank', 'noopener')} type='button'>Visit branded URL</button>
              <button className='btn btn-primary btn-sm' onClick={finish} type='button'>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddDomainWizard;
